import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, Check } from "lucide-react";
import api from "@/lib/api";

const TIERS = [
  { name: "Title Partner", desc: "Exclusive continental naming rights and premier brand integration." },
  { name: "Diamond Partner", desc: "Headline visibility across broadcast, digital and the coronation." },
  { name: "Gold Partner", desc: "Premium logo placement and delegation activations." },
  { name: "Country Partner", desc: "Sponsor a specific delegation and its national journey." },
  { name: "Fashion Partner", desc: "Dress the candidates and own the runway moments." },
  { name: "Technology Partner", desc: "Power the platform and the digital fan experience." },
];

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [form, setForm] = useState({ company: "", email: "", budget: "", package: "" });
  useEffect(() => { api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sponsor-inquiry", { company: form.company, email: form.email, budget: form.budget, package: form.package });
      toast.success("Inquiry received!", { description: "Our partnerships team will reach out within 48 hours." });
      setForm({ company: "", email: "", budget: "", package: "" });
    } catch { toast.error("Please check your details and try again"); }
  };

  return (
    <div className="section py-12 sm:py-16" data-testid="sponsors-page">
      <p className="kicker mb-2">Partnership Marketplace</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50">Sponsors</h1>
      <p className="mt-3 max-w-2xl text-amber-100/60">Align your brand with the most ambitious celebration of African beauty, culture and leadership.</p>

      <p className="kicker mb-6 mt-12">Current Partners</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {sponsors.map((s) => (
          <div key={s.id} className="card-lux flex flex-col items-center gap-2 p-6 text-center">
            <Award className="h-6 w-6 text-gold/70" />
            <p className="text-sm text-amber-50">{s.name}</p>
            <p className="text-[0.6rem] uppercase tracking-wider text-muted-foreground">{s.tier}</p>
          </div>
        ))}
      </div>

      <p className="kicker mb-6 mt-14">Sponsorship Tiers</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {TIERS.map((t) => (
          <div key={t.name} className="card-lux p-6">
            <h3 className="font-serif text-xl gold-text">{t.name}</h3>
            <p className="mt-2 text-sm text-amber-100/60">{t.desc}</p>
            <p className="mt-4 flex items-center gap-2 text-xs text-emerald-accent"><Check className="h-3.5 w-3.5" /> Benefits & pricing configurable by HQ</p>
          </div>
        ))}
      </div>

      <div className="mt-14 glass rounded-2xl p-8" id="become-a-sponsor">
        <h2 className="text-3xl text-amber-50">Become a Sponsor</h2>
        <p className="mt-2 text-amber-100/60">Tell us about your brand and we'll craft a tailored partnership.</p>
        <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="sponsor-form">
          <input required placeholder="Company name" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="sponsor-company" className="rounded-lg border border-gold/25 bg-obsidian px-4 py-3 text-sm text-amber-50 outline-none focus:border-gold" />
          <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sponsor-email" className="rounded-lg border border-gold/25 bg-obsidian px-4 py-3 text-sm text-amber-50 outline-none focus:border-gold" />
          <select value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="rounded-lg border border-gold/25 bg-obsidian px-4 py-3 text-sm text-amber-50 outline-none focus:border-gold">
            <option value="">Budget range</option><option>$10k – $50k</option><option>$50k – $250k</option><option>$250k+</option>
          </select>
          <select value={form.package} onChange={(e) => setForm({ ...form, package: e.target.value })} className="rounded-lg border border-gold/25 bg-obsidian px-4 py-3 text-sm text-amber-50 outline-none focus:border-gold">
            <option value="">Interested tier</option>{TIERS.map((t) => <option key={t.name}>{t.name}</option>)}
          </select>
          <button className="btn-gold sm:col-span-2" data-testid="sponsor-submit">Submit Inquiry</button>
        </form>
      </div>
    </div>
  );
}
